<template>
  <div id="report">
    <AddressInfoWrapper
      id="address"
      key="Address information"
      :has-additional-info="addressPart?.formattedData?.coinFamily == 'btc'"
      :info="addressPart"
      :loading="reportPartsLoading"
    />

    <div
      id="risks"
      class="report__block"
    >
      <div
        class="report__block-wrapper report__risk-block-wrapper"
        :class="{
          'report__risk-block-wrapper--skeleton': !getPurchasedInfo.risks,
        }"
      >
        <RiskScore :score="risksPart?.data?.totalScore" />

        <ProfileInfo
          :loading="reportPartsLoading"
          :profile-info="ownerPart.data"
        />
        <FundsBlock
          :incoming-sources="incomingSourcesWithFunds"
          :outgoing-sources="outgoingSourcesWithFunds"
        />
        <ReportBlockFooter
          v-if="isMobile"
          type="risks"
        />
      </div>
    </div>

    <div
      id="tier2"
      class="report__block"
    >
      <div
        class="report__block-wrapper"
        :class="{
          'report__tier2-block-wrapper--skeleton':
            !getPurchasedInfo.tier2 ||
            (!getPurchasedInfo.full && getActivePartBar.value == 'entity'),
        }"
      >
        <ReportBlockHeader
          title="Money Trail"
          :type="getBarsTypeBuyButton"
        />
        <ReportBarsWrapper
          :all-data-source="allDataSource"
          :all-data-source-by-owner="allDataSourceByOwner"
          :essential="false"
          :loading="reportPartsLoading"
          :pure-sources="pureSources"
          :selected-token="selectedToken"
        />
        <ReportBlockFooter
          v-if="isMobile"
          :type="getBarsTypeBuyButton"
        />
      </div>
    </div>

    <ReportTablesWrapper
      id="full"
      class="report__block"
      :class="{
        'report__full-block-wrapper--skeleton': !getPurchasedInfo.full,
      }"
      :currencies-by-direction="currenciesByDirection"
      :current-score-error-message="currentScoreErrorMessage"
      :essential="false"
      :loading="reportPartsLoading"
      :pure-sources="pureSources"
      report-type="address"
      :selected-token="selectedToken"
    />
  </div>
</template>

<script>
// Components
import AddressInfoWrapper from '@/pages/report/components/info/AddressInfoWrapper.vue'
import ProfileInfo from '@/pages/report/components/profile-info/ProfileInfo'
import ReportTablesWrapper from '@/pages/report/components/gl-report/table/ReportTablesWrapper'
import ReportBlockHeader from '@/pages/report/components/section-components/ReportBlockHeader'
import ReportBlockFooter from '@/pages/report/components/section-components/ReportBlockFooter'
import RiskScore from '@/pages/report/components/risks-info/RiskScore'
import FundsBlock from '@/pages/report/components/risks-info/FundsBlock'
// Vuex
import { mapGetters, mapState } from 'vuex'
//mixins
import deviceWidthMixin from '@/assets/mixins/deviceWidthMixin'
// GL report
import ReportBarsWrapper from '@/pages/report/components/gl-report/bars/ReportBarsWrapper'

export default {
  components: {
    AddressInfoWrapper,
    ProfileInfo,
    ReportBarsWrapper,
    ReportTablesWrapper,
    ReportBlockHeader,
    ReportBlockFooter,
    RiskScore,
    FundsBlock,
  },
  mixins: [deviceWidthMixin],
  props: {
    allDataSource: {
      type: Object,
      default: () => [],
    },
    allDataSourceByOwner: {
      type: Object,
      default: () => [],
    },
    reportPartsLoading: {
      type: Boolean,
      default: false,
    },
    addressPart: {
      type: Object,
      default: () => ({}),
    },
    ownerPart: {
      type: Object,
      default: () => ({}),
    },
    risksPart: {
      type: Object,
      default: () => ({}),
    },
    fullDetailsPart: {
      type: Object,
      default: () => ({}),
    },
    profileInfo2: {
      type: Object,
      default: () => ({}),
    },
    totalFunds: {
      type: [Number, String],
      default: '',
    },
    pureSources: {
      type: Object,
      default: () => ({}),
    },
    selectedToken: {
      type: Object,
      default: () => ({}),
    },
    currenciesByDirection: {
      type: Object,
      default: () => ({}),
    },
    currentScoreErrorMessage: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    ...mapState('analytics', ['coinData', 'coinAmountInfo']),
    ...mapGetters('report', [
      'getActiveType',
      'getActiveFunds',
      'getPurchasedInfo',
      'getNoSentTransactions',
    ]),
    ...mapGetters('directions', ['getActivePartBar']),
    hasTxs() {
      return Boolean(
        this.addressPart &&
          this.addressPart.data &&
          this.addressPart.data.numberOfTxs,
      )
    },
    showScoreInChart() {
      return this.hasTxs || this.totalFunds != null
    },
    incomingSourcesWithFunds() {
      if (!this.risksPart?.data?.score?.sof) return []
      return this.risksPart.data.score.sof.map(item => ({
        ...item,
        funds: {
          name: item.name,
          score: item.score,
        },
      }))
    },
    outgoingSourcesWithFunds() {
      if (!this.risksPart?.data?.score?.uof) return []
      return this.risksPart.data.score.uof.map(item => ({
        ...item,
        funds: {
          name: item.name,
          score: item.score,
        },
      }))
    },
    getBarsTypeBuyButton() {
      return this.getActivePartBar.value === 'entity'
        ? 'full' 
        : 'tier2'
    },
  },
}
</script>

<style>
.report__block + .report__block {
  margin-top: 24px;
}
.report__block-wrapper {
  padding: 24px;
  background-color: var(--white);
  border-radius: var(--Corner-radius-XL);
  position: relative;
}
.report__block-wrapper + .report__block-wrapper {
  margin-top: 24px;
}
.report__block-header-title {
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  color: var(--Basic-900);
}
.report__block-sub-header {
  margin-bottom: 8px;
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
  color: var(--Basic-900);
}

.report__risk-block-wrapper {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* transactions */
.report-bars__wrapper {
  margin-bottom: 24px;
}
.report-tables__wrapper .gl-section-block__body-wrapper {
  padding: 0;
}
.gl-section-block__body-wrapper--white {
  padding: 0;
  border: none;
}
.report-sources__table .o-table__th:last-child,
.report-sources__table .o-table__td:last-child {
  padding-right: 0 !important;
}
.report-sources__table .pp-pagination-table {
  padding-right: 0 !important;
  padding-left: 8px;
}

@media (max-width: 767px) {
  .gl-highlight.active:has(.report-address-info__wrapper) {
    padding: 10px;
    background: white;
  }
  .report__block-wrapper {
    border-radius: initial;
  }
  .report__block + .report__block {
    margin-top: initial;
    border-top: 1px solid var(--Basic-200, rgba(9, 21, 64, 0.08));
  }
}
</style>

<style scoped>
.report__risk-block-wrapper--skeleton,
.report__tier2-block-wrapper--skeleton,
.report__full-block-wrapper--skeleton {
  user-select: none;
}

.report__risk-block-wrapper--skeleton::v-deep .entity-icon img {
  visibility: hidden;
}
.report__risk-block-wrapper--skeleton::v-deep .entity-icon {
  background: var(--Basic-200, rgba(9, 21, 64, 0.08));
}
.report__risk-block-wrapper--skeleton::v-deep .gl-row__wrapper,
.report__risk-block-wrapper--skeleton::v-deep .gl-badge-info__text,
.report__tier2-block-wrapper--skeleton::v-deep
  .risk-exposure-header__amount
  div,
.report__tier2-block-wrapper--skeleton::v-deep .risk-exposure__row-badge-text,
.report__full-block-wrapper--skeleton::v-deep .pp-pagination-table__left-count,
.report__full-block-wrapper--skeleton::v-deep .report-sources__table-owner,
.report__full-block-wrapper--skeleton::v-deep .report-sources__table-label,
.report__full-block-wrapper--skeleton::v-deep .report-sources__table--root-tx,
.report__full-block-wrapper--skeleton::v-deep .report-sources__table--leaf-tx,
.report__full-block-wrapper--skeleton::v-deep .report-sources__table--depth,
.report__full-block-wrapper--skeleton::v-deep .pp-date__date,
.report__full-block-wrapper--skeleton::v-deep .pp-date__time {
  border-radius: 16px;
  background: var(--Basic-200, rgba(9, 21, 64, 0.08));
  color: transparent;
}

.report__full-block-wrapper--skeleton::v-deep .report-sources__table-owner-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.report__risk-block-wrapper--skeleton::v-deep .gl-badge-info {
  background-color: var(--Basic-100, rgba(9, 21, 64, 0.04)) !important;
  padding-right: 5px;
}
.report__risk-block-wrapper--skeleton::v-deep .gl-risk-info,
.report__tier2-block-wrapper--skeleton::v-deep .gl-risk-info,
.report__full-block-wrapper--skeleton::v-deep .gl-risk-info {
  background: var(--Basic-200, rgba(9, 21, 64, 0.08)) !important;
}
.report__risk-block-wrapper--skeleton::v-deep .gl-risk-info__value,
.report__tier2-block-wrapper--skeleton::v-deep .gl-risk-info__value,
.report__full-block-wrapper--skeleton::v-deep .gl-risk-info__value {
  color: transparent;
}

.report__risk-block-wrapper--skeleton::v-deep .gl-badge-info__text {
  line-height: 14px;
}

.report__risk-block-wrapper--skeleton::v-deep .score-status__percent,
.report__tier2-block-wrapper--skeleton::v-deep
  .risk-exposure-header__value-switcher,
.report__tier2-block-wrapper--skeleton::v-deep .risk-exposure__values-switcher,
.report__tier2-block-wrapper--skeleton::v-deep
  .risk-exposure__values-percents
  > div:not(.gl-skeleton-amount__wrapper),
.report__tier2-block-wrapper--skeleton::v-deep .risk-exposure__row-cntrl,
.report__tier2-block-wrapper--skeleton::v-deep
  .risk-exposure__row-info-percents-item
  > span,
.report__full-block-wrapper--skeleton::v-deep
  .report-sources__table--amount
  > span,
.report__full-block-wrapper--skeleton::v-deep
  .report-sources__table--price
  > span,
.report__full-block-wrapper--skeleton::v-deep
  .report-sources__table--share
  > span,
.report__full-block-wrapper--skeleton::v-deep .gl-menu-item,
.report__full-block-wrapper--skeleton::v-deep
  .report-sources__table--depth-label {
  display: none;
}

.report__tier2-block-wrapper--skeleton::v-deep
  .risk-exposure-header__amount
  div {
  width: 80px;
}
.report__tier2-block-wrapper--skeleton::v-deep .gl-skeleton-amount__value {
  width: 160px;
  height: 20px;
}
.report__tier2-block-wrapper--skeleton::v-deep .risk-exposure__row-badge-text {
  display: inline-block;
  width: 96px;
  height: 20px;
}

.report__tier2-block-wrapper--skeleton::v-deep
  .risk-exposure__row-info-percents-item--direct {
  background: var(--Basic-200, rgba(9, 21, 64, 0.08));
}
.report__tier2-block-wrapper--skeleton::v-deep
  .risk-exposure__row-info-percents-item--indirect {
  background: var(--Basic-100, rgba(9, 21, 64, 0.04));
}

.report__tier2-block-wrapper--skeleton::v-deep
  .risk-exposure__table
  .gl-skeleton-amount__value {
  width: 88px;
  height: 14px;
  flex-shrink: 0;
}

.report__tier2-block-wrapper--skeleton::v-deep
  .risk-exposure__row-info
  .gl-skeleton-amount__percent {
  margin-top: 4px;
}
.report__tier2-block-wrapper--skeleton::v-deep
  .gl-skeleton-amount__percent
  .gl-skeleton-amount__value {
  width: 24px;
  height: 14px;
  flex-shrink: 0;
}

.report__tier2-block-wrapper--skeleton::v-deep .report-bars-main__wrapper,
.report__full-block-wrapper--skeleton::v-deep table tbody {
  cursor: unset;
  pointer-events: none;
}

.report__full-block-wrapper--skeleton.report-tables__wrapper {
  min-height: initial;
}

.report__full-block-wrapper--skeleton::v-deep .pp-pagination-table__right {
  display: none;
}

.report__full-block-wrapper--skeleton::v-deep .pp-pagination-table__left-count {
  display: inline-block;
  width: 48px;
  line-height: 16px;
}

.report__full-block-wrapper--skeleton::v-deep .report-sources__table-owner {
  display: inline-block;
  width: 96px;
  height: 16px;
  line-height: 16px;
}
.report__full-block-wrapper--skeleton::v-deep .report-sources__table-label {
  width: 48px;
  height: 12px;
  line-height: 12px;
}
.report__full-block-wrapper--skeleton::v-deep .gl-skeleton-amount__amount {
  font-size: 14px;
}
.report__full-block-wrapper--skeleton::v-deep .report-sources__table--share {
  line-height: 14px;
}
.report__full-block-wrapper--skeleton::v-deep .report-sources__table--root-tx,
.report__full-block-wrapper--skeleton::v-deep .report-sources__table--leaf-tx {
  width: 72px;
  height: 16px;
}
.report__full-block-wrapper--skeleton::v-deep .pp-date__date {
  width: 96px;
  height: 16px;
}
.report__full-block-wrapper--skeleton::v-deep .pp-date__time {
  width: 60px;
  height: 12px;
  color: transparent !important;
  margin-left: auto;
  margin-right: 0;
}
</style>
